import React, { useEffect, useMemo, useRef, useState } from "react";

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "ابحث...",
  disabled = false,
  loading = false,
  noResultsText = "لا توجد نتائج",
  dir = "rtl",
  className = "",
  dropdownMaxHeight = 240,
  enableOther = true,
  otherValue = "__OTHER__",
  otherLabel = "اخرى",
  dataLabel,
  name,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [customMode, setCustomMode] = useState(false);
  const [hadTypedCustom, setHadTypedCustom] = useState(false); // NEW

  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const fire = (type = 'input') => {
    const el = inputRef.current;
    if (!el) return;
    el.dispatchEvent(new Event(type, { bubbles: true })); // ليشتغل الـ listener في الفورم
  };
  const clearInvalidClasses = () => {
    const el = inputRef.current;
    if (!el) return;
    el.classList.remove('is-invalid');
    const wrap = el.closest('.addr-input-wrap, .ss-control, .addr-field');
    if (wrap) wrap.classList.remove('is-invalid');
  };

  const valueInOptions = useMemo(
    () => options.some((o) => String(o.value) === String(value)),
    [options, value]
  );

  // Enter/leave custom mode based on external value
  useEffect(() => {
    const inCustom = enableOther && !!value && !valueInOptions;
    setCustomMode(inCustom);
    if (!inCustom) setHadTypedCustom(false); // reset flag when exiting custom mode
  }, [value, valueInOptions, enableOther]);

  // Reset query when external value changes (except in custom mode)
  useEffect(() => {
    if (!customMode) setQuery("");
  }, [value, customMode]);

  // Put "اخرى" FIRST (if enabled and not already present)
  const baseList = useMemo(() => {
    if (!enableOther) return options;
    const hasOther = options.some((o) => String(o.value) === String(otherValue));
    const otherOpt = { value: otherValue, label: otherLabel };
    return hasOther ? options : [otherOpt, ...options];
  }, [options, enableOther, otherValue, otherLabel]);

  // Arabic-aware normalization
  const normalizeAr = (s) => s?.toString().toLowerCase().replace(/[أإآ]/g, "ا") ?? "";

  const filtered = useMemo(() => {
    const nq = normalizeAr(query.trim());
    if (!nq) return baseList;
    return baseList.filter((o) => normalizeAr(o.label).includes(nq));
  }, [baseList, query]);

  // Click outside to close
  useEffect(() => {
    const fn = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const currentLabel = useMemo(() => {
    if (customMode) return value ?? "";
    const opt = options.find((o) => String(o.value) === String(value));
    return opt?.label || "";
  }, [options, value, customMode]);

  // Focus when entering custom mode with dropdown open
  useEffect(() => {
    if (!open || !customMode) return;
    const id = requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const len = el.value.length;
        try {
          el.setSelectionRange(len, len);
        } catch {}
      }
    });
    return () => cancelAnimationFrame(id);
  }, [open, customMode]);

  const commitOption = (opt) => {
    if (!opt) return;

    if (enableOther && String(opt.value) === String(otherValue)) {
      // Switch to free-text mode; keep dropdown open and focus input
      setCustomMode(true);
      setHadTypedCustom(false); // user hasn't typed yet
      setOpen(true);
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }

    onChange?.(opt.value ?? "");
    setOpen(false);
    setQuery("");
    setActiveIndex(-1);
    setCustomMode(false);
    setHadTypedCustom(false);

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setCustomValidity('');
        inputRef.current.checkValidity();
      }
      clearInvalidClasses();
      fire('input');
      fire('change');
    }, 0);
  };

  const commitCustom = (val) => {
    const v = (typeof val === "string" ? val : query).trim();
    onChange?.(v);
    setOpen(false);
    setActiveIndex(-1);

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setCustomValidity('');
        inputRef.current.checkValidity();
      }
      clearInvalidClasses();
      fire('input');
      fire('change');
    }, 0);
  };

  // Auto-commit after 2s of inactivity in custom mode,
  // and only exit to list when user had typed then cleared.
  useEffect(() => {
    if (!customMode) return;

    const v = query.trim();

    // Track if the user ever typed something (non-empty) in this custom session
    if (v && !hadTypedCustom) setHadTypedCustom(true);

    // If user cleared after previously typing → exit to list
    if (v === "" && hadTypedCustom) {
      setCustomMode(false);
      setHadTypedCustom(false);
      setQuery("");
      onChange?.("");
      setOpen(true); // reopen options
      return;
    }

    // If user is still typing (non-empty) and focused → auto-commit after 2s
    if (v !== "") {
      const isFocused = document.activeElement === inputRef.current;
      if (isFocused) {
        const t = setTimeout(() => {
          commitCustom(v);
        }, 2000);
        return () => clearTimeout(t);
      }
    }
  }, [customMode, query, hadTypedCustom]);

  const onKeyDown = (e) => {
    if (customMode) {
      if (e.key === "Enter") {
        e.preventDefault();
        commitCustom();
      } else if (e.key === "Escape") {
        setOpen(false);
      }
      return;
    }

    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowDown") {
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActiveIndex((i) => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter") {
      if (open && activeIndex >= 0 && filtered[activeIndex]) {
        commitOption(filtered[activeIndex]);
      }
      e.preventDefault();
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleInputBlur = () => {
    // Defer to allow option clicks to fire first
    setTimeout(() => {
      if (customMode) {
        const v = query.trim();
        if (v) {
          onChange?.(v); // trigger geocoding/hoods fetch immediately
          setOpen(false);
          setActiveIndex(-1);
        } else if (hadTypedCustom) {
          // Only revert to list if user had typed before clearing
          setCustomMode(false);
          setHadTypedCustom(false);
          setQuery("");
          onChange?.("");
          setOpen(true);
        } else {
          // User selected "اخرى" and then blured without typing — just close
          setOpen(false);
        }
      } else {
        setOpen(false);
      }
    }, 0);
  };

  const handleControlClick = () => {
    if (disabled) return;
    setOpen(true);
  };

  return (
    <div ref={wrapRef} className={`ss-wrap ${className}`} dir={dir}>
      <div className={`ss-control ${disabled ? "is-disabled" : ""}`} onClick={handleControlClick}>
        <input
          key={customMode ? "custom" : "normal"} // remount when mode changes → autofocus works
          ref={inputRef}
          className="ss-input"
          autoFocus={customMode}
          placeholder={loading ? "جارٍ التحميل..." : customMode ? "اكتب اسمًا..." : placeholder}
          value={customMode ? query : open ? query : currentLabel}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={handleInputBlur}
          onKeyDown={onKeyDown}
          disabled={disabled || loading}
          aria-expanded={open}
          aria-autocomplete="list"
          data-label={dataLabel}
          name={name}
          required
        />
        <span className="ss-caret">▾</span>
      </div>

      {open && (
        <div className="ss-dropdown" role="listbox" style={{ maxHeight: dropdownMaxHeight }}>
          {loading ? (
            <div className="ss-empty">جارٍ التحميل...</div>
          ) : customMode ? (
            <div className="ss-empty">اكتب الاسم؛ سيُثبت تلقائيًا بعد لحظات</div>
          ) : filtered.length === 0 ? (
            <div className="ss-empty">{noResultsText}</div>
          ) : (
            filtered.map((opt, i) => (
              <div
                key={opt.value}
                className={`ss-option ${String(opt.value) === String(value) ? "is-selected" : ""} ${
                  i === activeIndex ? "is-active" : ""
                }`}
                role="option"
                onMouseDown={(e) => {
                  e.preventDefault();
                  commitOption(opt);
                }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
